/**
 * The chat panel's words (ADR-068 decision 8 and its amendments). Every sentence here is a draft for the author's
 * look at `/ui` (ADR-061's amendment "the export is a visual reference"). Each sentence is in sentence case and ends
 * with a full stop, and each label, button and placeholder is in sentence case with none (R-0027, ADR-068's
 * amendment "chat's sentences, and soft errors that stand apart"). None says zone, session, map, gateway or channel
 * (ADR-060, ADR-061's copy rules). ADR-090 adds `/afk`'s description and its lines, settled in its review, and the
 * whisper's line, which is the author's. ADR-068's amendment "Global, Trade and Help" adds the channels' names, the
 * line's lead, the lines about joining, leaving and mutes, their refusals and the commands' descriptions.
 *
 * A number is handed in as the text the page prints (`{seconds}`, `{max}`, the parts of `muteTime`), so a wait of
 * 1234 s reads "1234 s". `{families}` and `{steps}` are lists the page joins with a comma and a space.
 * `/ping`'s panel words its numbers through `pingMs`, `pingRange` and the three `pingSpoken` sentences, which one
 * message for assistive technology joins with a space; `{value}`, `{min}` and `{max}` are numbers as the page prints
 * them, or a measure already worded.
 */
export const CHAT = {
  panel: "Chat",
  tabs: "Chat scopes",
  allTab: "All",
  unread: "{scope}, {count} unread",
  input: "Chat message",
  placeholder: "Press Enter to speak",
  placeholderTouch: "Tap to speak",
  send: "Send",
  pickerList: "Send to",
  minimise: "Minimise chat",
  openChat: "Open chat",
  closeChat: "Close chat",
  openChatUnread: "Open chat, new messages",
  grip: "Resize chat. Drag, press the arrow keys, or choose Chat size in Settings",
  gripTouch: "Resize chat. Drag, or choose Chat size in Settings",
  picker: {
    nowhere: "Choose where to send",
    scope: "Sending to {scope}. Choose where to send",
  },
  commandScope: "Command",
  commandsList: "Commands",
  namesList: "Suggestions",
  command: "That is not a chat command.",
  target: "Whisper a character by its first and family names: /w First Family message.",
  family: "Name a family to block or unblock: /block Family.",
  nobody: "No one has whispered you recently.",
  notConnected: "Chat is not connected, so nothing was sent.",
  unknownRefusal: "That message was not sent.",
  noBlocks: "You have blocked no family.",
  nowAfk: "You are now AFK.",
  noLongerAfk: "You are no longer AFK.",
  afkWhisper: "{name} is AFK, so replies may be delayed.",
  replyToNobody: "No one to reply to yet",
  replyTarget: "Reply to {name}",
  replyNobody: "Nobody to reply to",
  channelWord: "Name Global, Trade or Help: /join trade.",
  muteUsage: "Mute a family for a time: /mute Family 30m, then Global, Trade or Help, or nothing for all three.",
  channelLead: "[{channel}]",
  joined: "You joined {channel}.",
  left: "You left {channel}.",
  alreadyJoined: "You are already in {channel}.",
  alreadyLeft: "You already left {channel}.",
  notJoined: "You left {channel}. Type /join {word} to join it again.",
  muted: "You are muted in {scope} for {time}.",
  unmuted: "You are no longer muted in {scope}.",
  familyMuted: "The {family} family is muted in {scope} for {time}.",
  familyUnmuted: "The {family} family is no longer muted in {scope}.",
  blockList: "Blocked families: {families}.",
  blocked: "You blocked the {family} family.",
  unblocked: "You unblocked the {family} family.",
  alreadyBlocked: "The {family} family is already blocked.",
  throttled: "You can send again in {seconds} s.",
  lookupWait: "Names can be looked up again in {seconds} s.",
  whisperLead: {
    sent: "To",
    received: "From",
  },
  replyTo: "To {name}",
  pinging: "Pinging server: {steps}.",
  noReply: "No reply from the server.",
  pingRunning: "A ping is already running.",
  pingTitle: "Ping",
  pingReplies: "{replies} of {sent} replies",
  pingNetwork: "Network",
  pingSocket: "Socket",
  pingGame: "Game response",
  pingNetworkMeaning: "The network's round trip to the server, timed by the browser",
  pingSocketMeaning: "The game connection's round trip, as the game sees it",
  pingGameMeaning: "How much longer the game takes to answer than the network",
  notAvailable: "not available",
  pingMs: "{value} ms",
  pingRange: "(min {min}, max {max})",
  pingHeader: "{title} · {replies}",
  pingSpokenTitle: "{title}, {replies}.",
  pingSpokenMeasure: "{label} {value} average, min {min}, max {max}.",
  pingSpokenValue: "{label} {value}.",
  scopes: {
    local: "Local",
    region: "Region",
    global: "Global",
    trade: "Trade",
    help: "Help",
    party: "Party",
    whisper: "Whisper",
    guild: "Guild",
    duel: "Duel",
    system: "System",
    broadcast: "Broadcast",
    raid: "Raid",
    officer: "Officer",
  },
  allChannels: "{global}, {trade} and {help}",
  muteTime: {
    minutes: "{minutes} min",
    hours: "{hours} h",
    days: "{days} days",
  },
  card: {
    level: "Lv {level}",
    online: "Online",
    afk: "AFK",
  },
  groups: {
    chat: "Chat",
    channels: "Channels",
    party: "Party",
    social: "Social",
  },
  describe: {
    whisper: "Whisper to a player",
    reply: "Whisper back to the last player who whispered you",
    pose: "Say what your character does, to Local",
    block: "Block a family's lines and whispers",
    unblock: "Lift a block",
    blocks: "List the families you blocked",
    clear: "Clear the chat panel",
    ping: "Measure the round trip to the server",
    afk: "Mark yourself AFK",
    global: "Speak to every player in Global",
    trade: "Buy and sell with every player in Trade",
    help: "Ask or answer a question in Help",
    party: "Speak to your party, wherever its members are",
    join: "Join Global, Trade or Help",
    leave: "Leave Global, Trade, Help or your party",
    invite: "Invite a player to your party",
    remove: "Remove a member from your party, for its leader",
    promote: "Hand your party's lead to a member, for its leader",
    grant: "Let a member invite to your party, for its leader",
    mute: "Mute a family in Global, Trade or Help, for GMs",
    unmute: "Lift a family's mute, for GMs",
  },
  option: {
    whisper: { first: "A player's first and family names", second: "What to whisper" },
    reply: { first: "What to whisper back" },
    pose: { first: "What your character does, as in waves" },
    block: { first: "The family to block" },
    unblock: { first: "A family on your block list" },
    global: { first: "What to say to every player" },
    trade: { first: "What you buy or sell, and for how much" },
    help: { first: "Your question, or your answer to one" },
    party: { first: "What to say to your party" },
    join: { first: "Global, Trade or Help" },
    leave: { first: "Global, Trade, Help or party" },
    invite: { first: "The player's first and family names" },
    remove: { first: "The member's first and family names" },
    promote: { first: "The member's first and family names" },
    grant: { first: "The member's first and family names", second: "On gives the right, off takes it back" },
    mute: { first: "The family to mute", second: "How long: 30m, 2h or 1d", third: "Global, Trade or Help; none for all three" },
    unmute: { first: "The family to unmute", second: "Global, Trade or Help; none for all three" },
  },
  closed: {
    command: "/{word} is not open yet.",
    channel: "{channel} chat is not open yet.",
  },
  refused: {
    changes: "Too many changes. Try again in {seconds} s.",
    empty: "There is nothing to send.",
    long: "A message is at most {max} characters.",
    characters: "That message holds a character chat does not take.",
    absent: "No one of that name is in the world.",
    self: "You cannot whisper yourself.",
    blocked: "You have blocked that family.",
    busy: "They are receiving too many whispers; try again in a moment.",
    family: "No family of that name exists.",
    own: "That is your own family.",
    full: "Your block list is full.",
    unlisted: "That family is not on your block list.",
    account: "A character with no family cannot do that.",
    unavailable: "That could not be saved. Try again.",
    channel: "There is no such chat.",
    unjoined: "You are not in that chat. Type /join and its name to join it.",
    muted: "You are muted there for {time}.",
    pace: "That chat is slowed. You can speak there again in {seconds} s.",
    repeated: "You said that there a moment ago.",
    forbidden: "You cannot do that.",
    duration: "A mute that long needs a moderator.",
    unmuted: "That family is not muted there.",
    emote: "You do not have that emote.",
    target: "Your target is gone.",
    alone: "You are not in a party.",
  },
} as const;
